/**
 * Promo Bar Modal Manager
 * Handles opening/closing of promo bar modal and manages focus trap
 */
class PromoBarManager {
    constructor() {
        this.promoBar = document.querySelector('.promo__bar_main');
        this.overlay = document.querySelector('.promo-modal__overlay');
        this.modal = document.querySelector('.promo-modal');
        this.closeButtons = document.querySelectorAll('.close');
        this.seeDetailsButtons = document.querySelectorAll('.see-details');
        this.backButtons = document.querySelectorAll('.back-btn');
        this.detailsSections = document.querySelectorAll('.details');
        
        this.init();
    }

    init() {
        this.bindEvents();
        this.setupFocusTrap();
    }

    bindEvents() {
        // Open modal when clicking elements with data-open-modal="offers-modal"
        document.addEventListener('click', (e) => {
            if (e.target.closest('[data-open-modal="offers-modal"]')) {
                e.preventDefault();
                this.openModal();
            }
        });

        // Close modal events
        this.closeButtons.forEach(button => {
            button.addEventListener('click', () => this.closeModal());
        });

        // Overlay click to close
        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.closeModal());
        }

        // See details buttons
        this.seeDetailsButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const buttonElement = e.currentTarget; // Use currentTarget instead of target
                const detailsSection = buttonElement.closest('li').querySelector('.details');
                this.showDetails(detailsSection);
            });
        });

        // Back buttons
        this.backButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const detailsSection = e.currentTarget.closest('.details');
                this.hideDetails(detailsSection);
            });
        });

        // Escape key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeModal();
            }
        });
    }

    openModal() {
        if (!this.promoBar) return;
        
        // Remove hidden and close classes, add open class
        this.promoBar.classList.remove('tw-hidden', 'close');
        this.promoBar.classList.add('open');
        this.overlay.classList.add('active');
        this.modal.classList.add('active');
        
       
        // Set modal attributes
        if (this.modal) {
            this.modal.setAttribute('aria-hidden', 'false');
            this.modal.setAttribute('tabindex', '0');
        }
        
        // Focus first focusable element
        const firstFocusable = this.modal?.querySelector('.first-focusable-element');
        if (firstFocusable) {
            firstFocusable.focus();
        }
        
        // Prevent body scroll
        document.body.style.overflow = 'hidden';
    }

    closeModal() {
        if (!this.promoBar) return;

        this.overlay.classList.remove('active');
        this.modal.classList.remove('active');
        
        // Hide all details sections first
        this.detailsSections.forEach(section => {
            this.hideDetails(section);
        });
        
        // Show the main promo-modal__list again when closing
        const mainPromoList = this.modal?.querySelector('.promo-modal__list:not(.details .promo-modal__list)');
        if (mainPromoList) {
            mainPromoList.classList.remove('tw-hidden');
        }
        
        // Add hidden and close classes, remove open class
        this.promoBar.classList.add('tw-hidden', 'close');
        this.promoBar.classList.remove('open');
        
        // Set modal attributes
        if (this.modal) {
            this.modal.setAttribute('aria-hidden', 'true');
            this.modal.setAttribute('tabindex', '-1');
        }
        
        // Restore body scroll
        document.body.style.overflow = '';
    }

    showDetails(detailsSection) {
        if (!detailsSection) return;
        
        // Hide all other details sections
        this.detailsSections.forEach(section => {
            if (section !== detailsSection) {
                this.hideDetails(section);
            }
        });
        
            
        // Show the selected details section
        detailsSection.classList.remove('tw-hidden');
        detailsSection.classList.add('active');
        detailsSection.setAttribute('aria-hidden', 'false');
        
        // Show the promo-modal__list inside the details section
        const detailsPromoList = detailsSection.querySelector('.promo-modal__list');
        if (detailsPromoList) {
            detailsPromoList.classList.remove('tw-hidden');
        }
        
        // Focus first focusable element in details
        const firstFocusable = detailsSection.querySelector('.details-first-focusable-element');
        if (firstFocusable) {
            firstFocusable.focus();
        }
    }

    hideDetails(detailsSection) {
        if (!detailsSection) return;
        
        detailsSection.classList.add('tw-hidden');
        detailsSection.classList.remove('active');
        detailsSection.setAttribute('aria-hidden', 'true');
        
        // Hide the promo-modal__list inside the details section
        const detailsPromoList = detailsSection.querySelector('.promo-modal__list');
        if (detailsPromoList) {
            detailsPromoList.classList.add('tw-hidden');
        }
        
        // Show the main promo-modal__list when hiding details
        const mainPromoList = this.modal?.querySelector('.promo-modal__list:not(.details .promo-modal__list)');
        if (mainPromoList) {
            mainPromoList.classList.remove('tw-hidden');
        }
    }

    setupFocusTrap() {
        if (!this.modal) return;

        const firstFocusable = this.modal.querySelector('.first-focusable-element');
        const lastFocusable = this.modal.querySelector('.last-focusable-element');

        if (firstFocusable && lastFocusable) {
            this.modal.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') {
                    if (e.shiftKey) {
                        if (document.activeElement === firstFocusable) {
                            e.preventDefault();
                            lastFocusable.focus();
                        }
                    } else {
                        if (document.activeElement === lastFocusable) {
                            e.preventDefault();
                            firstFocusable.focus();
                        }
                    }
                }
            });
        }

        // Setup focus trap for details sections
        this.detailsSections.forEach(detailsSection => {
            const detailsFirstFocusable = detailsSection.querySelector('.details-first-focusable-element');
            const detailsLastFocusable = detailsSection.querySelector('.details-last-focusable-element');

            if (detailsFirstFocusable && detailsLastFocusable) {
                detailsSection.addEventListener('keydown', (e) => {
                    if (e.key === 'Tab') {
                        if (e.shiftKey) {
                            if (document.activeElement === detailsFirstFocusable) {
                                e.preventDefault();
                                detailsLastFocusable.focus();
                            }
                        } else {
                            if (document.activeElement === detailsLastFocusable) {
                                e.preventDefault();
                                detailsFirstFocusable.focus();
                            }
                        }
                    }
                });
            }
        });
    }
}

// Initialize the promo bar manager when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new PromoBarManager();
});

